import { Fragment } from "react";
import { ColumnDef } from "@tanstack/react-table";
import { EnumBadge } from "@/components/badges";
import { truncateText, formatDate, formatPersonName } from "@/lib/formatters";
import { getCaseStatusChainSegments } from "@/lib/data-transform";
import { TRUNCATE_CASE_ID } from "@/lib/constants";
import type { CaseRecord } from "@/types";

export const caseColumns: ColumnDef<CaseRecord>[] = [
  {
    accessorKey: "id",
    header: "Case ID",
    cell: ({ row }) => (
      <span className="font-mono text-xs" title={row.original.id}>
        {truncateText(row.original.id, TRUNCATE_CASE_ID)}
      </span>
    ),
  },
  {
    id: "name",
    header: "Name",
    cell: ({ row }) => {
      const c = row.original;
      return (
        <span>
          {formatPersonName(
            c.member_first_name || c["Member First Name"],
            c.member_last_name || c["Member Last Name"]
          ) || "-"}
        </span>
      );
    },
  },
  {
    accessorKey: "payer_name",
    header: "Payer",
    cell: ({ row }) => (
      <span>{row.original.payer_name || row.original["Payer Name"] || "-"}</span>
    ),
  },
  {
    accessorKey: "product_name",
    header: "Medication",
    cell: ({ row }) => {
      const name =
        row.original.product_name ||
        row.original["Product Name #1 (Human)"] ||
        row.original["Product Name #1"] ||
        "-";
      return (
        <span className="max-w-[200px] truncate block" title={name}>
          {name}
        </span>
      );
    },
  },
  {
    id: "outbound_scheduled_for",
    header: "Scheduled call",
    cell: ({ row }) => {
      const v = row.original.outbound_scheduled_for;
      return (
        <span className="text-sm text-muted-foreground">
          {v ? formatDate(v) : "—"}
        </span>
      );
    },
  },
  {
    id: "status",
    header: "Status",
    cell: ({ row }) => {
      const segments = getCaseStatusChainSegments(row.original);
      const title = segments.map((s) => s.label).join(" → ");
      return (
        <div
          className="flex min-w-[280px] max-w-[520px] flex-wrap items-center gap-x-1.5 gap-y-1 py-0.5"
          title={title}
        >
          {segments.map((seg, i) => (
            <Fragment key={`${row.original.id}-status-${i}`}>
              {i > 0 ? (
                <span className="text-muted-foreground select-none" aria-hidden>
                  →
                </span>
              ) : null}
              <EnumBadge value={seg.label} semanticTone={seg.tone} />
            </Fragment>
          ))}
        </div>
      );
    },
  },
  {
    accessorKey: "pa_status",
    header: "PA Status",
    cell: ({ row }) =>
      row.original.pa_status ? (
        <EnumBadge value={row.original.pa_status} />
      ) : (
        <span className="text-muted-foreground">-</span>
      ),
  },
  {
    accessorKey: "callAttempts",
    header: "Attempts",
    cell: ({ row }) => (
      <span className="font-medium">{row.original.callAttempts ?? 0}</span>
    ),
  },
  {
    id: "voice_provider",
    header: "Provider",
    cell: ({ row }) => {
      const c = row.original;
      const current = c.voice_provider;
      const override = c.voice_provider_override;
      if (!current && !override) {
        return <span className="text-muted-foreground">—</span>;
      }
      const label = (v: string | null | undefined) =>
        v === "asepha_voice" ? "asepha-voice" : v ?? "—";
      return (
        <div className="flex items-center gap-1 text-xs">
          <EnumBadge value={label(current)} />
          {override && override !== current ? (
            <>
              <span className="text-muted-foreground" aria-hidden>
                →
              </span>
              <EnumBadge value={label(override)} semanticTone="info" />
            </>
          ) : null}
        </div>
      );
    },
  },
  {
    accessorKey: "created_at",
    header: "Created",
    cell: ({ row }) => (
      <span className="text-sm">{formatDate(row.original.created_at)}</span>
    ),
  },
];
